import React, { useEffect, useState } from 'react';
import { BasicPageContainer } from "components/views/BasicPageContainer";
import { Box, Typography, FormControlLabel, Checkbox, TextField, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePatchWorkspaceMfa } from 'hooks/usePatchWorkspaceMfa';
import AuthGuard from 'context/auth/AuthGuard';
import { UserRoles } from 'constants/userRoles';
import { useWorkspaceInfo } from 'hooks/useWorkspaceInfo';

export const WorkspaceManagementPage = () => {
    const { t } = useTranslation();
    const { workspaceInfo, loading: workspaceLoading, editingWorkspaceName, editWorkspaceName } = useWorkspaceInfo();
    const { patchMfa, patching } = usePatchWorkspaceMfa();
    const [workspaceName, setWorkspaceName] = useState(workspaceInfo?.name || '');
    const [mfaEnabled, setMfaEnabled] = useState(workspaceInfo?.mfaEnabled || false);
    const [newWorkspaceName, setNewWorkspaceName] = useState(workspaceName);
    const [isEditing, setIsEditing] = useState(false);

    const handleChangeMfa = () => {
        patchMfa(!mfaEnabled)
            .then((r) => {
                if (r?.status === 200) {
                    setMfaEnabled(!mfaEnabled);
                }
            })
            .catch(error => {
                console.error('Failed to update MFA setting:', error);
            });
    }

    const handleChangeName = () => {
        editWorkspaceName({ name: newWorkspaceName })
            .then((r) => {
                if (r?.status === 200) {
                    setWorkspaceName(newWorkspaceName);
                    setNewWorkspaceName(newWorkspaceName);
                    setIsEditing(false);
                }
            });
    }

    useEffect(() => {
        if (workspaceInfo) {
            setWorkspaceName(workspaceInfo.name);
            setMfaEnabled(workspaceInfo.mfaEnabled);
            setNewWorkspaceName(workspaceInfo.name);
        }
    }, [workspaceInfo]);

    return (
        <AuthGuard requiredRoles={[UserRoles.ADMIN]}>
            <BasicPageContainer>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="60vh"
                    gap={2}
                >
                    <Typography variant="h5" mb={2}>
                        {t('mfaManagement.title', 'Multi-Factor Authentication Settings')}
                    </Typography>
                    <Typography variant="body1" mb={2} color="textSecondary" align="center">
                        {t('mfaManagement.description', 'Enable or disable multi-factor authentication for this workspace.')}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={mfaEnabled}
                                onChange={handleChangeMfa}
                                disabled={patching}
                                color="primary"
                            />
                        }
                        label={t('mfaManagement.enableMfa', 'Enable MFA')}
                    />

                    <Box mt={4} width="100%" maxWidth={400}>
                        <Typography variant="h6" mb={1}>
                            {t('workspaceManagement.workspaceName', 'Workspace Name')}
                        </Typography>
                        {isEditing ? (
                            <Box display="flex" gap={1}>
                                <TextField
                                    value={newWorkspaceName}
                                    onChange={e => setNewWorkspaceName(e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleChangeName}
                                    disabled={!newWorkspaceName.trim()}
                                >
                                    {t('common.save', 'Save')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                        setNewWorkspaceName(workspaceName);
                                        setIsEditing(false);
                                    }}
                                >
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                            </Box>
                        ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography>{workspaceName}</Typography>
                                <Button
                                    variant="text"
                                    onClick={() => setIsEditing(true)}
                                >
                                    {t('common.edit', 'Edit')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </BasicPageContainer>
        </AuthGuard>
    );
};